import React, { useContext, useEffect, useState, FormEvent, useCallback } from "react";
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DraggableModalDialog } from './DraggableModalDialog';
import { useHttpUtils } from '../Utilities/useHttpUtils';
import useAlert from '../Alerts/useAlert';
import CommentDialog from './CommentDialog';
import { convertToDateTime, convertLocalTimeZoneToUTC } from "../Utilities/useDateUtils";
import DateTimePicker from "../Components/DateTimePicker";
import { CreateNewLot } from "../Types/Lot";
import { RequiredDialogInput, RequiredDialogSelect } from "../DialogInputs";
import { handleInputChange } from "../Utilities/DialogUtils";
import WithComment from "../Types/WithComment";
import { DateTime } from "luxon";
import { useUserAssignedBusinessUnits } from "../Utilities/useUserAssignedBusinessUnits";
import { useRecipes } from "../Utilities/Hooks";
import { Recipe } from "../Types/Recipe";
import ConfigurationContext from "../Contexts/ConfigurationContext";
import { BusinessUnit } from "../Types/BusinessUnit";

export interface CreateNewLotDialogProps {
    showDialog: boolean,
    closeDialog: () => void
}

export interface CreateNewLotDialogData {
    lotNumber: string,
    businessUnitId: string,
    recipeId: string,
    buildStart: DateTime,
    buildEnd: DateTime
}

export default function CreateNewLotDialog({ showDialog, closeDialog }: CreateNewLotDialogProps) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { t } = useTranslation();

    const defaultLot = {
        lotNumber: "",
        businessUnitId: "",
        recipeId: "",
        buildStart: DateTime.now().startOf("day"),
        buildEnd: DateTime.now().startOf("day")
    };

    const [loading, setLoading] = useState<boolean>(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [formValidated, setFormValidated] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [dialogData, setDialogData] = useState<CreateNewLotDialogData>(defaultLot);
    const { userAssignedBusinessUnits } = useUserAssignedBusinessUnits();
    const { configurationData } = useContext(ConfigurationContext);
    const [businessUnitsThatCanCreateLots, setBusinessUnitsThatCanCreateLots] = useState<BusinessUnit[]>([]);
    const recipes: Recipe[] = useRecipes();
    const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>();

    useEffect(() => {
        if (configurationData && userAssignedBusinessUnits) {
            setBusinessUnitsThatCanCreateLots(
                userAssignedBusinessUnits.filter(bu =>
                    configurationData.some(config =>
                        config.name === "CanManuallyCreateLot" &&
                        config.value === "true" &&
                        config.businessUnitId === bu.businessUnitId
                    )
                )
            );
        }

    }, [configurationData, userAssignedBusinessUnits]);

    useEffect(() => {
        const sortedRecipesForBusinessUnit = recipes
            .filter(x => x.businessUnitId === Number(dialogData.businessUnitId))
            .sort((a, b) => a.name.localeCompare(b.name));

        setFilteredRecipes(sortedRecipesForBusinessUnit);
    },[dialogData.businessUnitId, recipes]);

    const handleSave = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === true) {
            setShowCommentDialog(true);
        }

        setFormValidated(true);
    };

    const closeDialogAndResetForm = useCallback(() => {
        setDialogData(defaultLot);
        setFormValidated(false);
        closeDialog();
    }, [closeDialog]);

    const createNewLot = useCallback(async (comment: string) => {
        let url = `/api/v1/lot/create`;
        let body: WithComment<CreateNewLot> = {
            data: {
                lotNumber: dialogData?.lotNumber ?? "",
                businessUnitId: Number(dialogData?.businessUnitId),
                recipeId: Number(dialogData?.recipeId),
                buildStartUtc: convertLocalTimeZoneToUTC(dialogData?.buildStart),
                buildEndUtc: convertLocalTimeZoneToUTC(dialogData?.buildEnd)
            },
            comment: comment
        };

        setLoading(true);
        await fetchDataAuthenticated(url, 'POST', JSON.stringify(body))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Created new lot ${dialogData.lotNumber}`);
                    // go to new lot
                    closeDialogAndResetForm();
                } else {
                    let errorMessage = await response.text();
                    throw new Error("Failed to create lot: " + errorMessage);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [closeDialogAndResetForm, dialogData, fetchDataAuthenticated, setAlert]);

    const onChange = (e: any) => {
        handleInputChange(e, dialogData, setDialogData, errors, setErrors);
    };

    return (
        <>
            <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialogAndResetForm} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t<string>("Dialogs.CreateNewLot.Title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {
                        loading
                            ?
                            <div className="d-flex justify-content-center mb-2">
                                <Spinner animation="border" />
                            </div>
                            :
                            <Form id="createNewLot" noValidate validated={formValidated} onSubmit={handleSave}>
                                <RequiredDialogInput controlId="formLotNumber" title={t<string>("Dialogs.CreateNewLot.LotNumber")} name="lotNumber" value={dialogData?.lotNumber} onChange={onChange} error={errors.lotNumber} />
                                <RequiredDialogSelect controlId="formBusinessUnit" title="Business Unit" name="businessUnitId" value={dialogData.businessUnitId} onChange={onChange} error={errors.businessUnitId}>
                                    <option value="" key={0} >{t<string>("Application.Controls.DefaultSelect")}</option>
                                    {businessUnitsThatCanCreateLots.map((bu) => <option value={bu.businessUnitId} key={bu.businessUnitId}>{bu.code}</option>)}
                                </RequiredDialogSelect>
                                <RequiredDialogSelect controlId="formGrade" title="Grade" name="recipeId" value={dialogData.recipeId} onChange={onChange} error={errors.recipeId} disabled={dialogData.businessUnitId === undefined}>
                                    <option value="" key={0} >{t<string>("Application.Controls.DefaultSelect")}</option>
                                    {filteredRecipes && filteredRecipes.map((recipe) => <option value={recipe.recipeId} key={recipe.recipeId}>{recipe.name}</option>)}
                                </RequiredDialogSelect>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label column>{t<string>("Dialogs.CreateNewLot.BuildStart")}</Form.Label>
                                    <Col sm={8}>
                                        <DateTimePicker required name="buildStart" dateTime={dialogData?.buildStart ? new Date(dialogData.buildStart.toISO({ includeOffset: false }) ?? "") : null} handleDateSelect={(date) => date && setDialogData({ ...dialogData, buildStart: convertToDateTime(date) })} />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-2">
                                    <Form.Label column>{t<string>("Dialogs.CreateNewLot.BuildEnd")}</Form.Label>
                                    <Col sm={8}>
                                        <DateTimePicker required name="buildEnd" dateTime={dialogData?.buildEnd ? new Date(dialogData.buildEnd.toISO({ includeOffset: false }) ?? "") : null} handleDateSelect={(date) => date && setDialogData({ ...dialogData, buildEnd: convertToDateTime(date) })} />
                                    </Col>
                                </Form.Group>
                            </Form>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialogAndResetForm} disabled={loading}>
                        {t<string>("Application.Controls.Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" form="createNewLot" disabled={loading}>
                        {t<string>("Application.Controls.Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={createNewLot} />
        </>
    );
}
